import React, {
  useState,
  cloneElement,
  ReactElement,
  useRef,
  useEffect,
} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { cn } from "@/utils/cn";
import {
  FolderIcon,
  FoldersIcon,
  PlusBlackIcon,
  CheckCircleActiveIcon,
  CheckCircleIcon,
} from "@/components/ui/icon";
import { useToast } from "@/components/ui/use-toast";
import { creationVideoManager } from "@/manager";
import { useCreationProduct } from "@/hooks/use-creation-product";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

const ChooseProjectModalSkeleton: React.FC = () => {
  return (
    <div className={cn("flex flex-col flex-1 overflow-hidden")}>
      {/* All Folders 标题行骨架屏 */}
      <div className={cn("flex items-center justify-between mb-3 pl-3 pr-6")}>
        <div className={cn("flex items-center gap-2")}>
          {/* 文件夹图标骨架屏 */}
          <div className={cn("w-5 h-5 bg-gray-200 rounded animate-pulse")} />
          {/* 标题文字骨架屏 */}
          <div className={cn("w-20 h-4 bg-gray-200 rounded animate-pulse")} />
        </div>
        {/* 加号按钮骨架屏 */}
        <div className={cn("w-5 h-5 bg-gray-200 rounded animate-pulse")} />
      </div>

      {/* 项目列表骨架屏 */}
      <div className={cn("flex-1 space-y-1 overflow-hidden")}>
        {/* 生成 5 个项目骨架屏项 */}
        {Array.from({ length: 5 }).map((_, index) => (
          <div
            key={index}
            className={cn("flex items-center gap-2 pl-10 pr-6 h-10 rounded-sm")}
          >
            {/* 文件夹图标骨架屏 */}
            <div className={cn("w-5 h-5 bg-gray-200 rounded animate-pulse")} />
            {/* 项目名称骨架屏 */}
            <div
              className={cn("flex-1 h-3 bg-gray-200 rounded animate-pulse")}
              style={{ width: `${60 + Math.random() * 40}%` }} // 随机宽度模拟不同长度的项目名
            />
            {/* 选择图标骨架屏 */}
            <div
              className={cn("w-5 h-5 bg-gray-200 rounded-full animate-pulse")}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

interface ChooseProjectModalProps {
  actor: Actor;
  trigger: ReactElement;
}

export const ChooseProjectModal: React.FC<ChooseProjectModalProps> = ({
  actor,
  trigger,
}) => {
  const { t } = useTranslation();
  const { showToastByCode } = useToast();
  const {
    init,
    uncategorizedProjects,
    projectOriginIdMap,
    createProject,
    renameProject,
    reorderProjects,
    toProject,
  } = useCreationProduct();
  const [open, setOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 初始化
  useEffect(() => {
    if (open) {
      setLoading(true);
      init(false).finally(() => {
        setLoading(false);
      });
    }
  }, [open]);

  // 处理输入框聚焦和选中
  useEffect(() => {
    if (editingProjectId && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingProjectId]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  const handleTriggerClick = () => {
    setOpen(true);
    setSelectedProjectId(null);
  };

  const handleConfirm = () => {
    toProject(uncategorizedProjects.find((p) => p.id === selectedProjectId));
    creationVideoManager.updateActors(
      [actor],
      projectOriginIdMap[selectedProjectId]
    );
    setOpen(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    setOpen(false);
    setSelectedProjectId(null);
  };

  // 开始编辑项目名称
  const startEditing = (project: Project) => {
    setEditingProjectId(project.id);
    setEditName(project.name);
  };

  // 处理单击
  const handleSingleClick = (projectId: string) => {
    setSelectedProjectId(projectId);
  };

  // 处理双击
  const handleDoubleClick = (project: Project) => {
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
    startEditing(project);
  };

  // 处理项目点击（区分单双击）
  const handleProjectClick = (project: Project) => {
    if (editingProjectId) return;

    if (clickTimeoutRef.current) {
      // 双击
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
      handleDoubleClick(project);
    } else {
      // 单击
      clickTimeoutRef.current = setTimeout(() => {
        handleSingleClick(project.id);
        clickTimeoutRef.current = null;
      }, 200);
    }
  };

  // 提交编辑
  const handleSubmitEdit = async () => {
    if (!editingProjectId || !editName.trim()) {
      handleCancelEdit();
      return;
    }

    const project = uncategorizedProjects.find(
      (p) => p.id === editingProjectId
    );
    if (project && editName.trim() !== project.name) {
      try {
        await renameProject(project, editName.trim());
      } catch (error) {
        showToastByCode(error);
      }
    }

    setEditingProjectId(null);
    setEditName("");
  };

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingProjectId(null);
    setEditName("");
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCancelEdit();
    }
  };

  // 处理失去焦点
  const handleBlur = () => {
    handleSubmitEdit();
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        {cloneElement(trigger, {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            handleTriggerClick();
            // 保留原有的 onClick 事件
            if (trigger.props.onClick) {
              trigger.props.onClick(e);
            }
          },
        })}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className={cn("fixed inset-0 bg-black/50 z-50")} />
        <Dialog.Content
          className={cn(
            "fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-[18px] shadow-lg w-96 h-[31.25rem] p-6 overflow-hidden z-50 flex flex-col"
          )}
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          {/* 头部 */}
          <div className={cn("flex items-center justify-between mb-8")}>
            <Dialog.Title className={cn("text-lg text-i-normal font-semibold")}>
              {t("choose_project_modal.title")}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={cn(
                  "p-1 hover:bg-gray-100 rounded transition-colors"
                )}
                onClick={handleCancel}
              >
                <X className={cn("w-4 h-4")} />
              </button>
            </Dialog.Close>
          </div>
          {loading && <ChooseProjectModalSkeleton />}
          {/* 文件夹列表 */}
          {loading || (
            <div className={cn("flex flex-col flex-1 overflow-y-auto")}>
              {/* All Folders 标题行 */}
              <div
                className={cn(
                  "flex items-center justify-between mb-3 pl-3 pr-6"
                )}
              >
                <div
                  className={cn(
                    "flex items-center gap-2 text-base font-medium text-i-black"
                  )}
                >
                  <FoldersIcon className={cn("w-5 h-5")} />
                  <span>{t("choose_project_modal.all_projects")}</span>
                </div>
                <button
                  onClick={() => createProject(null, false)}
                  className={cn("hover:bg-gray-100 rounded transition-colors")}
                >
                  <PlusBlackIcon className={cn("w-5 h-5")} />
                </button>
              </div>

              {/* 项目列表 */}
              <div className={cn("flex-1 space-y-1 overflow-y-auto")}>
                {uncategorizedProjects.map((project) => {
                  const isSelected = selectedProjectId === project.id;
                  const isEditing = editingProjectId === project.id;

                  return (
                    <div
                      key={project.id}
                      className={cn(
                        "flex items-center gap-2 pl-10 pr-6 h-10 rounded-sm text-base text-i-black font-medium cursor-pointer transition-colors",
                        isSelected
                          ? "bg-i-active text-i-active"
                          : "hover:bg-i-active"
                      )}
                      onClick={() => !isEditing && handleProjectClick(project)}
                    >
                      <FolderIcon className={cn("w-5 h-5")} />
                      {isEditing ? (
                        <input
                          ref={inputRef}
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={handleKeyDown}
                          onBlur={handleBlur}
                          className={cn(
                            "flex-1 bg-transparent border-none outline-none text-sm"
                          )}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span className={cn("flex-1 text-sm")}>
                          {project.name}
                        </span>
                      )}
                      {isSelected && !isEditing && (
                        <CheckCircleActiveIcon
                          className={cn("w-5 h-5 text-i-primary")}
                        />
                      )}
                      {!isSelected && !isEditing && (
                        <CheckCircleIcon className={cn("w-5 h-5")} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 底部按钮 */}
          <div className={cn("flex items-center justify-end gap-2")}>
            <button
              onClick={handleCancel}
              className={cn(
                "px-4 py-2.5 text-sm text-i-black font-medium border border-[rgba(208,213,221,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-colors"
              )}
            >
              {t("move_project_modal.cancel_button")}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedProjectId}
              className={cn(
                "px-4 py-2.5 text-sm text-i-white font-medium bg-i-primary hover:bg-[rgba(94,53,198,1)] disabled:bg-[rgba(233,215,254,1)] rounded-sm shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)] transition-colors cursor-pointer"
              )}
            >
              {t("move_project_modal.confirm_button")}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
